import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { log } from '@cccom/shared/logging';
import { Button, CCFormField, DebouncedButton } from '@cccom/shared/ui';
import { zCCString, zCCStringCharLimit } from '@cccom/shared/utils/common';
import { Form } from '@cccom/ui/src/components/shadcn/form';
import { Textarea } from '../../shadcn/textarea';

type CommentFormProps = {
  onSubmit: (data: CommentData) => Promise<void>;
  commentType?: 'ADD' | 'REPLY';
  maxLength?: number;
  defaultValue?: string;
  onCancel?: () => void;
};

const COMMENT_CHAR_LIMIT = 500;

const commentSchema = z.object({
  message: zCCString({ field: 'Comment' }).and(
    zCCStringCharLimit({ char_limit: COMMENT_CHAR_LIMIT })
  ),
});

type CommentData = z.infer<typeof commentSchema>;

export function CommentForm({
  onSubmit,
  maxLength = COMMENT_CHAR_LIMIT,
  commentType = 'ADD',
  defaultValue,
  onCancel,
}: CommentFormProps) {
  const [t] = useTranslation();

  const formMethods = useForm<CommentData>({
    resolver: zodResolver(commentSchema),
    defaultValues: { message: defaultValue || '' },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    formState: { isDirty, isValid },
    reset,
    control,
  } = formMethods;

  const handleSubmitComment = async (data: CommentData) => {
    try {
      await onSubmit(data);
      reset();
    } catch (error) {
      log.error('Failed to submit comment:', error);
    }
  };

  const cancelHandler = () => {
    reset();
    if (onCancel) onCancel();
  };

  return (
    <Form {...formMethods}>
      <div className="flex flex-col gap-5">
        <CCFormField control={control} name="message">
          {(field) => (
            <Textarea
              rows={4}
              maxLength={maxLength + 1}
              placeholder={
                commentType === 'ADD'
                  ? t('common.actions.add_a_comment')
                  : t('common.actions.reply')
              }
              data-cy="comment"
              {...field}
            />
          )}
        </CCFormField>

        <div className="mb-4 flex gap-2">
          <DebouncedButton
            size="sm"
            onClick={handleSubmit(handleSubmitComment)}
            disabled={!isDirty || !isValid}
          >
            {t('common.actions.save_comment')}
          </DebouncedButton>
          <Button
            size="sm"
            variant="outline"
            onClick={cancelHandler}
            disabled={!isDirty && !defaultValue}
          >
            {t('common.actions.cancel')}
          </Button>
        </div>
      </div>
    </Form>
  );
}
